import React, {Suspense, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {getYYYYMMddFormat, getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import Popper from '@di-shared/features/common/Popper';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useTripDayNa, {getWeekIndexOfDate, getWeeksOfMonth, isTripOnDay} from '@di-shared/features/history/date/useTripDay.na';
import {DrivingInfoTripProps} from '@di-shared/features/history/driving-info/DrivingInfoTrip/index';
import DrivingInfoTripContent from '@di-shared/features/history/driving-info/DrivingInfoTripContent';
import useDrivingInfoNa from '@di-shared/features/history/driving-info/useDrivingInfo.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {getWeekIndexOfMonth, isSameDate, isSameYearAndMonth, parseDateWithFormat, startDateOfMonth} from '@shared/dateUtils';

export default function ({vin, selectedDate, date, changeDate}: DrivingInfoTripProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
          date={date}
          changeDate={changeDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, date, changeDate}: DrivingInfoTripProps) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const navigate = useNavigate();
  const {verifiedDrivingDistances} = useDrivingInfoNa({vin});
  const {focusTripDate, minDate, tripDays} = useTripDayNa({
    vin,
    selectedDate,
    minPeriodDate: verifiedDrivingDistances[0].verifiedDate,
  });

  function getInitialDate() {
    if (date) {
      const parsedDate = parseDateWithFormat(date);
      if (parsedDate && isSameYearAndMonth(parsedDate, selectedDate)) {
        return parsedDate;
      }
    }
    return focusTripDate;
  }

  const [innerDate, setInnerDate] = useState(getInitialDate());

  function updateDateAndUrl(date: Date) {
    setInnerDate(date);
    if (changeDate) {
      changeDate(date);
    }
  }

  return (
    <div className="section_container section_recent_records">
      <div className="section_container_header type_plain">
        <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
        <DrivingInfoTripTooltip />
      </div>
      <div aria-hidden={true}>
        <ReactDatePicker
          inline
          calendarClassName={'show-header'}
          locale={getDateFnsLocale()}
          disabledKeyboardNavigation
          onChange={() => {}}
          renderCustomHeader={() => null}
          useWeekdaysShort
        />
      </div>
      <Swiper
        key={getYYYYMMFormat(innerDate)}
        initialSlide={getWeekIndexOfDate(innerDate)}
        style={{overflow: 'hidden'}}
        modules={[Pagination]}
        observeParents
        observer
      >
        {getWeeksOfMonth(innerDate).map((_, index) => {
          return (
            <SwiperSlide key={index}>
              {({isActive}) => (
                <div aria-hidden={!isActive}>
                  <ReactDatePicker
                    inline
                    monthAriaLabelPrefix={''}
                    key={getYYYYMMFormat(innerDate)}
                    locale={getDateFnsLocale()}
                    selected={innerDate}
                    minDate={minDate}
                    maxDate={today}
                    filterDate={(date) => isSameYearAndMonth(date, innerDate)}
                    disabledKeyboardNavigation
                    calendarClassName={`show-first-week-${index + 1}`}
                    weekDayClassName={() => 'react-datepicker--disabled'}
                    onChange={updateDateAndUrl}
                    onMonthChange={updateDateAndUrl}
                    renderCustomHeader={() => null}
                    renderDayContents={(dayOfMonth, date: Date) =>
                      !isSameYearAndMonth(date, innerDate) ? null : (
                        <>
                          {dayOfMonth}
                          {isTripOnDay(tripDays, dayOfMonth) && (
                            <span className="has_data">
                              <span className="blind">{t('ctn_home_information')}</span>
                            </span>
                          )}
                        </>
                      )
                    }
                  />
                </div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
      <DrivingInfoTripContent
        vin={vin}
        selectedDate={innerDate}
      />
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
        onClick={() => {
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view driveinfo',
            data_name: t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(innerDate, 'MMMM')}),
          });
          navigate(ROUTE_PATHS.HISTORY_$DATE.getFullPath(vin, getYYYYMMddFormat(innerDate)));
        }}
      >
        {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(innerDate, 'MMMM')})}
      </button>
    </div>
  );
}

function FallbackLoading() {
  const today = new Date();
  const {defaultDistanceSymbol, defaultSpeedSymbol} = useUnitConverter();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  return (
    <div className="section_container skeleton">
      <div className="section_container_header type_plain">
        <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
      </div>
      <ReactDatePicker
        inline
        calendarClassName={'show-header'}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => {}}
        renderCustomHeader={() => null}
        useWeekdaysShort
      />
      <ReactDatePicker
        inline
        calendarClassName={`show-first-week-1`}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => null}
        weekDayClassName={() => 'react-datepicker--disabled'}
        renderCustomHeader={() => null}
      />
      <div
        className="driving_summary skeleton"
        aria-hidden="true"
      >
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="date_today">{t('ctn_mytrips_today')}</span>
        </div>
        <dl className="driving_summary_list">
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('ctn_mytrips_time')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('ctn_mytrips_distance')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
            </dd>
          </div>
        </dl>
      </div>
      <div
        className="trip_info skeleton"
        aria-hidden="true"
      >
        <span className="blind">{t('ctn_mytrips_detail_resize')}</span>
        <div className="trip_info_head">
          <span className="trip_info_item">{DashedDatetimeFormatter.formatRange(today, today, 'time')}</span>
        </div>
      </div>
      <div className="statistics_record_area">
        <ul
          className="statistics_record_list skeleton"
          aria-hidden="true"
        >
          <li className="statistics_record_item">
            <span className="statistics_record_key">{t('ctn_mytrips_time')}</span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{t('ctn_mytrips_time_min_01')}</span>
            </span>
          </li>
          <li className="statistics_record_item">
            <span className="statistics_record_key">{t('ctn_mytrips_distance')}</span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{defaultDistanceSymbol}</span>
            </span>
          </li>
          <li className="statistics_record_item">
            <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
            </span>
          </li>
        </ul>
        <button
          type="button"
          className="btn_learn_more skeleton"
          aria-hidden="true"
        >
          {t('msg_mytrips_see_more_eu')}
          <span className="ic ic_arrow_forward_12px" />
        </button>
      </div>
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
      >
        {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(today, 'MMMM')})}
      </button>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const today = new Date();
  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);
  const weekIndex = getWeekIndexOfMonth(startDate);

  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="section_container">
      <div className="section_container_header type_plain">
        <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
        <DrivingInfoTripTooltip />
      </div>
      <ReactDatePicker
        inline
        calendarClassName={'show-header'}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => {}}
        renderCustomHeader={() => null}
        useWeekdaysShort
      />
      <ReactDatePicker
        inline
        calendarClassName={`show-first-week-${weekIndex}`}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        selected={startDate}
        onChange={() => null}
        weekDayClassName={() => 'react-datepicker--disabled'}
        renderCustomHeader={() => null}
      />
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(startDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">{t('ctn_mytrips_detail_resize')}</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_load_data')}</span>
        </div>
      </div>
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
      >
        {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(startDate, 'MMMM')})}
      </button>
    </div>
  );
}

function DrivingInfoTripTooltip() {
  const {t} = useInferableTranslation();
  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={3}
      tooltip={
        <div
          role="tooltip"
          tabIndex={0}
        >
          <p
            className="tooltip_text"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_recent_driving_history_tooltip_eu')}
          </p>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          className="btn_tooltip"
          aria-expanded="false"
          type="button"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          />
        </button>
      </div>
    </Popper>
  );
}
